<template>
  <form class="mx-0 flex flex-wrap" @submit.prevent="createChannel()">
    <div
      v-if="!embedLink"
      class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%] pb-4"
    >
      <label class="flex items-center gap-2 pb-2">
        <input
          v-model="mode"
          type="radio"
          value="embedded"
          :disabled="!embeddedEnabled"
        />
        <span>{{
          $t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP_EMBEDDED.MODE_LINK')
        }}</span>
      </label>
      <label class="flex items-center gap-2">
        <input v-model="mode" type="radio" value="manual" />
        <span>{{
          $t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP_EMBEDDED.MODE_MANUAL')
        }}</span>
      </label>
      <p
        v-if="!embeddedEnabled"
        class="text-sm text-slate-600 dark:text-slate-400 pt-2"
      >
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP_EMBEDDED.MISSING_CONFIG') }}
      </p>
    </div>

    <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
      <label :class="{ error: $v.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model.trim="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="$v.inboxName.$touch"
        />
        <span v-if="$v.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <template v-if="isManualMode && !embedLink">
      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: $v.phoneNumber.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
          <input
            v-model.trim="phoneNumber"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')
            "
            @blur="$v.phoneNumber.$touch"
          />
          <span v-if="$v.phoneNumber.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
          </span>
        </label>
      </div>
    </template>

    <div
      v-if="!embedLink"
      class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]"
    >
      <label :class="{ error: $v.appName.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.APP_NAME.LABEL') }}
        </span>
        <input
          v-model.trim="appName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.APP_NAME.PLACEHOLDER')"
          @blur="$v.appName.$touch"
        />
        <span v-if="$v.appName.$error" class="message">
          {{
            isManualMode
              ? $t('INBOX_MGMT.ADD.WHATSAPP.APP_NAME.ERROR')
              : $t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP_EMBEDDED.APP_NAME_ERROR')
          }}
        </span>
      </label>
    </div>

    <template v-if="isManualMode && !embedLink">
      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: $v.appId.$error }">
          <span>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.APP_ID.LABEL') }}
          </span>
          <input
            v-model.trim="appId"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.APP_ID.PLACEHOLDER')"
            @blur="$v.appId.$touch"
          />
          <span v-if="$v.appId.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.APP_ID.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: $v.appToken.$error }">
          <span>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.APP_TOKEN.LABEL') }}
          </span>
          <input
            v-model.trim="appToken"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.APP_TOKEN.PLACEHOLDER')"
            @blur="$v.appToken.$touch"
          />
          <span v-if="$v.appToken.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.APP_TOKEN.ERROR') }}
          </span>
        </label>
      </div>
    </template>

    <div
      v-if="embedLink"
      class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%] pb-4"
    >
      <p class="text-slate-700 dark:text-slate-200 mb-2">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP_EMBEDDED.LINK_READY') }}
      </p>
      <woot-code :script="embedLink" lang="html" />
      <div class="flex gap-2 mt-4">
        <woot-button
          type="button"
          color-scheme="primary"
          @click="openEmbedLink"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP_EMBEDDED.OPEN_LINK') }}
        </woot-button>
        <woot-button type="button" variant="smooth" @click="copyEmbedLink">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP_EMBEDDED.COPY_LINK') }}
        </woot-button>
      </div>
    </div>

    <p
      v-if="onboardingError"
      class="w-full text-red-600 dark:text-red-400 text-sm pb-2"
    >
      {{ onboardingError }}
    </p>

    <div class="w-full">
      <woot-submit-button
        v-if="!embedLink"
        :loading="uiFlags.isCreating"
        :button-text="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
      <woot-button
        v-else
        type="button"
        color-scheme="primary"
        @click="continueToAddAgents"
      >
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP_EMBEDDED.CONTINUE') }}
      </woot-button>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { required } from 'vuelidate/lib/validators';
import router from '../../../../index';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';

const gupshupEmbeddedAppName = value =>
  /^[A-Za-z0-9]{6,150}$/.test((value || '').trim());

export default {
  mixins: [alertMixin],
  data() {
    return {
      mode: 'embedded',
      embedLink: '',
      onboardingError: '',
      createdInboxId: null,
      inboxName: '',
      phoneNumber: '',
      appName: '',
      appId: '',
      appToken: '',
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    embeddedEnabled() {
      return window.chatwootConfig.gupshupEmbeddedSignupEnabled === true;
    },
    isManualMode() {
      return this.mode === 'manual';
    },
  },
  mounted() {
    if (!this.embeddedEnabled) {
      this.mode = 'manual';
    }
  },
  validations() {
    if (this.isManualMode) {
      return {
        inboxName: { required },
        phoneNumber: { required, isPhoneE164OrEmpty },
        appName: { required },
        appId: { required },
        appToken: { required },
      };
    }

    return {
      inboxName: { required },
      appName: { required, gupshupEmbeddedAppName },
    };
  },
  methods: {
    async createChannel() {
      if (this.embedLink) {
        return;
      }

      if (this.mode === 'embedded') {
        if (!this.embeddedEnabled) {
          return;
        }
        if (!gupshupEmbeddedAppName(this.appName)) {
          this.$v.appName.$touch();
          return;
        }
      }

      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      try {
        const channelPayload = this.isManualMode
          ? {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'gupshup',
              provider_config: {
                app_name: this.appName,
                app_id: this.appId,
                app_token: this.appToken,
              },
            }
          : {
              type: 'whatsapp',
              provider: 'gupshup',
              embedded_signup: true,
              provider_config: { app_name: this.appName },
            };

        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName,
            channel: channelPayload,
          }
        );

        this.createdInboxId = whatsappChannel.id;

        if (whatsappChannel.gupshup_onboarding_status === 'failed') {
          this.onboardingError =
            whatsappChannel.provider_config?.onboarding_error || '';
          router.replace({
            name: 'settings_inbox_show',
            params: { inboxId: whatsappChannel.id },
          });
          return;
        }

        if (whatsappChannel.gupshup_embed_link) {
          this.embedLink = whatsappChannel.gupshup_embed_link;
          return;
        }

        this.continueToAddAgents();
      } catch (error) {
        this.showAlert(
          error.message || this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      }
    },
    continueToAddAgents() {
      const inboxId = this.createdInboxId;
      if (!inboxId) {
        return;
      }

      router.replace({
        name: 'settings_inboxes_add_agents',
        params: {
          page: 'new',
          inbox_id: inboxId,
        },
      });
    },
    openEmbedLink() {
      if (this.embedLink) {
        window.open(this.embedLink, '_blank', 'noopener,noreferrer');
      }
    },
    async copyEmbedLink() {
      if (!this.embedLink) {
        return;
      }

      try {
        await navigator.clipboard.writeText(this.embedLink);
      } catch (error) {
        this.showAlert(
          error.message || this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>
